import { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  onSnapshot,
  updateDoc,
  orderBy,
  where,
  query,
  serverTimestamp,
  Timestamp,
  type DocumentReference,
  getDocs,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db } from '@/firebase';

interface Mail {
  id: string;
  customerEmail: string;
  subject: string;
}

type FetchState = "idle" | "loading" | "ok" | "error";

// メールメッセージはルート mail_messages にフラット保存され、IMAP連携時も
// mailId で紐付けられることを前提とする。
interface MailMessage {
  id: string;
  mailId: DocumentReference;
  direction: "in" | "out";
  from: string;
  to: string;
  body: string;
  status?: string;
  account?: DocumentReference;
  subject?: string;
  cc?: string;
  bcc?: string;
  attach?: string;
  sign?: string;
  createdAt?: Timestamp;
  sentAt?: Timestamp;
  sendStatus?: "sent" | "error" | "skipped" | "pending";
  sendError?: string;
  messageId?: string;
  sendStartedAt?: Timestamp;
  sendFinishedAt?: Timestamp;
  sendDebug?: string[];
  rawMailId?: string; // 受信メールのMessage-IDなど生データとの紐付け
  staffUserId?: string; // 送信担当者
  imapUid?: number; // Xserver IMAP連携時のUIDなど将来拡張用
}

interface MailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  attach?: string;
  sign?: string;
  sortNo?: number;
}

// 対象の mail ドキュメントを取得
async function fetchMail(mailId: string): Promise<Mail | null> {
  const ref = doc(db, "mail", mailId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...(snapshot.data() as Mail) };
}

function formatDate(timestamp?: Timestamp) {
  if (!timestamp) return "-";
  return timestamp.toDate().toLocaleString();
}

export default function MailDetailPage() {
  const { mailId } = useParams<{ mailId: string }>();
  const navigate = useNavigate();
  const [mail, setMail] = useState<Mail | null>(null);
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [userOptions, setUserOptions] = useState<{ id: string; name: string }[]>([]);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [composeForm, setComposeForm] = useState({
    status: "",
    accountId: "",
    from: "",
    to: "",
    subject: "",
    cc: "",
    bcc: "",
    body: "",
    attach: "",
    sign: "",
    templateId: "",
  });
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [debugState, setDebugState] = useState({
    mailFetch: "idle" as FetchState,
    mailFetchError: "",
    messagesFetch: "idle" as FetchState,
    messagesError: "",
    lastSnapshotAt: "",
    lastSnapshotSize: 0,
    lastSendError: "",
  });
  const [sendTrace, setSendTrace] = useState<string[]>([]);
  const statusHistoryRef = useRef<Record<string, string | undefined>>({});

  const logSendStep = (step: string) => {
    setSendTrace((prev) => {
      const next = [...prev, `${new Date().toLocaleString()} - ${step}`];
      // keep last 30 events to avoid unbounded growth
      return next.slice(-30);
    });
  };

  const handleSend = async () => {
    if (!mailId || !mail) return;
    const mailRef = doc(db, "mail", mailId);
    const body = composeForm.body.trim();
    const from = (composeForm.from || auth.currentUser?.email || "").trim();
    const to = composeForm.to.trim();
    const subject = composeForm.subject.trim();
    const cc = composeForm.cc.trim();
    const bcc = composeForm.bcc.trim();
    const attach = composeForm.attach.trim();
    const sign = composeForm.sign.trim();
    const status = composeForm.status.trim();
    if (!body || !to) {
      setSendError("To / 本文 は必須です。");
      return;
    }

    setSending(true);
    setSendError(null);
    setDebugState((prev) => ({ ...prev, lastSendError: "" }));
    logSendStep("送信ボタンクリック→送信フロー開始");

    const currentUser = auth.currentUser;
    const accountRef = composeForm.accountId ? doc(db, "User", composeForm.accountId) : undefined;
    const bodyWithSignature = sign ? `${body}\n\n${sign}` : body;
    const newMessage = {
      mailId: mailRef,
      direction: "out" as const,
      status,
      account: accountRef,
      from,
      to,
      subject,
      cc,
      bcc,
      body: bodyWithSignature,
      attach,
      sign,
      staffUserId: currentUser?.uid,
      createdAt: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, "mail_messages"), newMessage);
      logSendStep(`mail_messages に追加成功 (id: ${docRef.id})`);
      setMessages((prev) => [
        ...prev,
        {
          id: docRef.id,
          ...newMessage,
          createdAt: Timestamp.now(),
        },
      ]);
      await updateDoc(mailRef, {
        lastMessageAt: serverTimestamp(),
        status: "waiting",
      });
      logSendStep("mail の lastMessageAt/status を更新完了");
      setIsComposeOpen(false);
      setShowCcBcc(false);
      setComposeForm((prev) => ({
        ...prev,
        status: prev.status,
        accountId: prev.accountId,
        from,
        to,
        subject,
        cc,
        bcc,
        body: "",
        attach,
        sign,
      }));
    } catch (err) {
      console.error("Failed to send mail message:", err);
      setSendError("送信に失敗しました。");
      setDebugState((prev) => ({
        ...prev,
        lastSendError:
          err && typeof err === "object" && "message" in err
            ? String((err as Error).message)
            : String(err),
      }));
      logSendStep(
        `送信フローで例外発生: ${
          err && typeof err === "object" && "message" in err
            ? String((err as Error).message)
            : String(err)
        }`
      );
    } finally {
      setSending(false);
      logSendStep("送信フロー終了");
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (!mailId) return;
    const mailRef = doc(db, "mail", mailId);

    setLoading(true);
    setError(null);
    setDebugState((prev) => ({
      ...prev,
      mailFetch: "loading",
      mailFetchError: "",
      messagesFetch: "loading",
      messagesError: "",
      lastSnapshotAt: "",
      lastSnapshotSize: 0,
    }));

    // 1) メールドキュメントを取得
    fetchMail(mailId)
      .then((mailData) => {
        if (!isMounted) return;
        setMail(mailData);
        setDebugState((prev) => ({ ...prev, mailFetch: "ok" }));
      })
      .catch((err) => {
        console.error("Failed to fetch mail detail", err?.code, err?.message || err);
        if (isMounted) {
          setError("メール詳細の取得に失敗しました。");
          setDebugState((prev) => ({
            ...prev,
            mailFetch: "error",
            mailFetchError: `${err?.code || ""} ${err?.message || err}`.trim(),
          }));
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    // 2) メッセージ一覧をリアルタイム購読し、sendStatus 更新を反映
    const messagesRef = collection(db, "mail_messages");
    const messagesQuery = query(
      messagesRef,
      where("mailId", "==", mailRef),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        if (!isMounted) return;
        logSendStep(`onSnapshot 受信 (${snapshot.size} 件)`);
        const sortedMessages = snapshot.docs
          .map((doc) => ({ id: doc.id, ...(doc.data() as MailMessage) }))
          .sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
        sortedMessages.forEach((message) => {
          if (message.direction !== "out") return;
          const current = message.sendStatus || "pending";
          const prev = statusHistoryRef.current[message.id];
          if (prev !== current) {
            logSendStep(`送信ステータス更新: ${message.id} ${prev || "(初回)"} → ${current}`);
            statusHistoryRef.current[message.id] = current;
          }
        });
        setMessages(sortedMessages);
        setDebugState((prev) => ({
          ...prev,
          messagesFetch: "ok",
          lastSnapshotAt: new Date().toLocaleString(),
          lastSnapshotSize: snapshot.size,
        }));
      },
      (err) => {
        console.error("Failed to subscribe mail messages", err?.code, err?.message || err);
        if (isMounted) {
          setError("メール詳細の取得に失敗しました。");
          setDebugState((prev) => ({
            ...prev,
            messagesFetch: "error",
            messagesError: `${err?.code || ""} ${err?.message || err}`.trim(),
          }));
        }
      }
    );

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [mailId]);

  useEffect(() => {
    const preloadUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "User"));
        const users = usersSnapshot.docs.map((userDoc) => {
          const data = userDoc.data() as { display_name?: string; display_Name?: string; email?: string };
          return {
            id: userDoc.id,
            name: data.display_name || data.display_Name || data.email || userDoc.id,
          };
        });
        setUserOptions(users);
      } catch (err) {
        console.error("Failed to load users for mail compose", err);
      }
    };

    preloadUsers();
  }, []);

  useEffect(() => {
    const preloadTemplates = async () => {
      try {
        const templatesQuery = query(collection(db, "mail_templates"), orderBy("sortNo", "asc"));
        const snapshot = await getDocs(templatesQuery);
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<MailTemplate, "id">),
        }));
        setTemplates(data);
      } catch (err) {
        console.error("Failed to load mail templates", err);
      }
    };

    preloadTemplates();
  }, []);

  useEffect(() => {
    if (!mail) return;
    setComposeForm((prev) => ({
      ...prev,
      to: mail.customerEmail || prev.to,
      subject: mail.subject || prev.subject,
    }));
  }, [mail]);

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!mail) return <div className="p-4">メールが見つかりませんでした。</div>;

  const latestOutboundDebug = [...messages]
    .reverse()
    .find((m) => m.direction === "out" && (m.sendDebug?.length || m.sendStatus || m.sendError));

  return (
    <div className="p-4 space-y-4">
      <button className="text-blue-600 underline" onClick={() => navigate("/admin/mail")}>
        ← 一覧に戻る
      </button>
      <div className="border rounded p-3 bg-gray-50">
        <div className="text-lg font-semibold">{mail.subject}</div>
        <div className="text-sm text-gray-600">{mail.customerEmail}</div>
      </div>

      <div className="flex justify-end">
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700"
          onClick={() => {
            setSendError(null);
            setIsComposeOpen(true);
            setComposeForm((prev) => ({
              ...prev,
              from: auth.currentUser?.email || prev.from || "",
            }));
          }}
        >
          メール作成
        </button>
      </div>

      <div className="space-y-3">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.direction === "out" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-xl p-3 rounded shadow text-sm space-y-1 ${
                message.direction === "out" ? "bg-blue-50" : "bg-white"
              }`}
            >
              <div className="text-xs text-gray-500">
                {message.direction === "out" ? "スタッフ" : "お客様"}・{formatDate(message.createdAt)}
              </div>
              <div className="whitespace-pre-wrap">{message.body}</div>
              <div className="text-xs text-gray-400">
                from: {message.from} / to: {message.to}
              </div>
              <div className="pt-1 text-right">
                <button
                  className="text-xs text-blue-600 underline"
                  onClick={() => navigate(`/admin/mail/${mailId}/messages/${message.id}`)}
                >
                  詳細を見る
                </button>
              </div>
              {message.direction === "out" && (
                <div className="text-[11px] text-gray-500">
                  ステータス: {message.sendStatus || "pending"}
                  {message.messageId && ` / Message-ID: ${message.messageId}`}
                  {message.sendError && (
                    <span className="text-red-500"> / エラー: {message.sendError}</span>
                  )}
                  {message.sendStartedAt && ` / 開始: ${formatDate(message.sendStartedAt)}`}
                  {message.sendFinishedAt && ` / 終了: ${formatDate(message.sendFinishedAt)}`}
                </div>
              )}
              {message.direction === "out" && message.sendDebug?.length ? (
                <div className="mt-1 text-[11px] text-gray-500 space-y-0.5">
                  <div className="font-semibold">送信デバッグ</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {message.sendDebug.map((entry, idx) => (
                      <li key={`${message.id}-debug-${idx}`}>{entry}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        ))}
        {messages.length === 0 && <div className="text-sm text-gray-500">メッセージがありません。</div>}
      </div>

      {isComposeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                <span className="text-blue-600">✉️</span>
                メール作成
              </div>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setIsComposeOpen(false);
                  setShowCcBcc(false);
                }}
                aria-label="close compose"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="compose-template">
                  テンプレート
                </label>
                <select
                  id="compose-template"
                  className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.templateId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedTemplate = templates.find((template) => template.id === selectedId);
                    setComposeForm((prev) => ({
                      ...prev,
                      templateId: selectedId,
                      subject: selectedTemplate?.subject || prev.subject,
                      body: selectedTemplate?.body || prev.body,
                      attach: selectedTemplate?.attach || prev.attach,
                      sign: selectedTemplate?.sign || prev.sign,
                    }));
                  }}
                >
                  <option value="">テンプレートを選択</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  テンプレートの本文には {"{{"}変数{"}}"} を含められます。送信前に必要に応じて書き換えてください。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-status">
                    ステータス
                  </label>
                  <select
                    id="compose-status"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.status}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">選択してください</option>
                    <option value="open">open</option>
                    <option value="waiting">waiting</option>
                    <option value="closed">closed</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-account">
                    担当者
                  </label>
                  <select
                    id="compose-account"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.accountId}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, accountId: e.target.value }))}
                  >
                    <option value="">担当者を選択</option>
                    {userOptions.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="compose-to">
                  To
                </label>
                <input
                  id="compose-to"
                  type="email"
                  className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.to}
                  onChange={(e) => setComposeForm((prev) => ({ ...prev, to: e.target.value }))}
                  placeholder="受信者アドレス"
                />
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium text-blue-600"
                  onClick={() => setShowCcBcc((prev) => !prev)}
                >
                  <span className={`transition-transform ${showCcBcc ? "rotate-90" : "rotate-0"}`}>
                    ▶
                  </span>
                  CC / BCC
                </button>
                {showCcBcc && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700" htmlFor="compose-cc">
                        CC
                      </label>
                      <input
                        id="compose-cc"
                        type="text"
                        className="w-full rounded-lg border px-3 py-2"
                        value={composeForm.cc}
                        onChange={(e) => setComposeForm((prev) => ({ ...prev, cc: e.target.value }))}
                        placeholder="CC を入力"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700" htmlFor="compose-bcc">
                        BCC
                      </label>
                      <input
                        id="compose-bcc"
                        type="text"
                        className="w-full rounded-lg border px-3 py-2"
                        value={composeForm.bcc}
                        onChange={(e) => setComposeForm((prev) => ({ ...prev, bcc: e.target.value }))}
                        placeholder="BCC を入力"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="compose-subject">
                  件名
                </label>
                <input
                  id="compose-subject"
                  type="text"
                  className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder="件名を入力"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="compose-body">
                  本文
                </label>
                <div className="relative">
                  <textarea
                    id="compose-body"
                    className="w-full min-h-[200px] rounded-lg border px-3 py-2 resize"
                    value={composeForm.body}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, body: e.target.value }))}
                    placeholder="本文を入力してください"
                  />
                  <span className="absolute bottom-2 right-3 text-gray-300 select-none pointer-events-none">↘</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-attach">
                    添付
                  </label>
                  <input
                    id="compose-attach"
                    type="text"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.attach}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, attach: e.target.value }))}
                    placeholder="添付の情報"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-sign">
                    署名
                  </label>
                  <input
                    id="compose-sign"
                    type="text"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.sign}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, sign: e.target.value }))}
                    placeholder="署名を入力"
                  />
                </div>
              </div>

              {sendError && <div className="text-sm text-red-600">{sendError}</div>}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  onClick={() => {
                    setIsComposeOpen(false);
                    setShowCcBcc(false);
                  }}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white shadow hover:bg-blue-700 disabled:opacity-60"
                  onClick={handleSend}
                  disabled={sending}
                >
                  {sending ? "送信中..." : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border rounded p-3 bg-yellow-50 text-xs space-y-1">
        <div className="font-semibold">デバッグ情報（暫定表示）</div>
        <div>mailId: {mailId}</div>
        <div>mail fetch: {debugState.mailFetch} {debugState.mailFetchError && `(err: ${debugState.mailFetchError})`}</div>
        <div>
          messages fetch: {debugState.messagesFetch} {debugState.messagesError && `(err: ${debugState.messagesError})`}
        </div>
        <div>
          last snapshot: {debugState.lastSnapshotAt || "-"} / {debugState.lastSnapshotSize} 件
        </div>
        {debugState.lastSendError && <div className="text-red-600">last send error: {debugState.lastSendError}</div>}
        {sendTrace.length > 0 && (
          <div className="pt-1 space-y-1">
            <div className="font-semibold">送信フローのイベントログ</div>
            <ol className="list-decimal list-inside space-y-0.5">
              {sendTrace.map((entry, idx) => (
                <li key={`${entry}-${idx}`}>{entry}</li>
              ))}
            </ol>
          </div>
        )}
        {latestOutboundDebug?.sendDebug?.length ? (
          <div className="pt-2 space-y-1">
            <div className="font-semibold">Cloud Function 送信デバッグ (最新の送信)</div>
            <div className="text-gray-500">
              mail_messages/{latestOutboundDebug.id} に書き戻された送信ログ
            </div>
            <ol className="list-decimal list-inside space-y-0.5">
              {latestOutboundDebug.sendDebug.map((entry, idx) => (
                <li key={`${latestOutboundDebug.id}-latest-debug-${idx}`}>{entry}</li>
              ))}
            </ol>
          </div>
        ) : null}
        <div className="text-gray-500">
          ※ このブロックは調査用の一時表示です。問題判明後に削除予定。
        </div>
      </div>
    </div>
  );
}
